import { useCallback, useEffect, useMemo, useRef, useState, type JSX, type MouseEvent, type TouchEvent } from "react";
import type { components } from "../../api/schema";
import { loadClientEnv } from "../../config/env";
import { SegmentRenderer } from "./SegmentRenderer";
import type { SegmentRendererProps } from "./SegmentRenderer";
import type { CharacterEntry } from "./parseInlineMarkup";
import {
  ChoiceList,
  resolveChoiceThinkingCue,
  type ChoicePreview,
  type ChoiceVisualCue,
} from "./ChoiceList";
import { AdvanceButton } from "./AdvanceButton";
import { useSegmentReveal } from "./useSegmentReveal";
import { VisualNovelLogModal } from "./VisualNovelLogModal";
import { useReaderStylePresetContext } from "../reader-style/ReaderStylePresetProvider";
import { useSwipeGesture } from "../../components/ui/useSwipeGesture";
import { useLongPress } from "../../components/ui/useLongPress";
import { usePinchZoom } from "../../components/ui/usePinchZoom";
import type { ReaderFontSize } from "../reader-style/types";
import type { VisualNovelActorCue } from "./VisualNovelLayout";
import { resolveChoicePrompt } from "./resolveChoicePrompt";
import { resolveCutinImageUrl } from "./characterAssetPaths";

type Scene = components["schemas"]["Scene"];
type Character = components["schemas"]["Character"];

export type SceneViewProps = {
  scene: Scene;
  isPending: boolean;
  onSelectChoice: (choiceId: string) => void;
  onAdvance: () => void;
  /** キャラクター一覧。内部で Map に変換してセグメントの色付けに使う。省略時は空 Map 扱い。 */
  characters?: Character[];
  /** Sceneと同版の承認済み画像map。 */
  visualAssets?: components["schemas"]["VisualAssets"] | undefined;
  /**
   * 読み取り専用モード。true のとき ChoiceList / AdvanceButton のクリックを無効化する。
   * バックスクロール表示で過去シーンを選択不可で表示する際に使用する。
   * paragraph-reveal (Phase 4) では readOnly=true のとき auto reveal を無効化し
   * 全 segment 即時表示にフォールバックする (autoEnabled=false 経由)。
   */
  readOnly?: boolean;
  /**
   * mobile-pwa Phase 4.3: 長押しで呼ばれる callback。 reader が長押しで通報 modal
   * を起動する用途。 未指定なら long-press 機構は無効。 readOnly のときは
   * 配線しても呼ばれない (= backscroll 履歴は通報対象外)。
   */
  onLongPress?: () => void;
  /** visual_novel mode 用。true のとき auto timer を張らず click/tap だけで次 segment を表示する。 */
  manualReveal?: boolean;
  /** 親レイアウトから click/tap 進行を通知する単調増加 signal。 */
  advanceSignal?: number | undefined;
  /** SceneView 自身の click で reveal を進めるか。親レイアウトが click を持つ場合は false。 */
  clickAdvanceEnabled?: boolean;
  /** セグメントの見た目。visual_novel では名前欄付きの固定テキスト領域向け表示にする。 */
  segmentVariant?: SegmentRendererProps["variant"];
  /** visual_novel では現在の1文だけを表示し、ログはモーダルに分離する。 */
  segmentDisplayMode?: "accumulate" | "current";
  /** 表示済み文章を読むログボタンを表示する。 */
  logEnabled?: boolean;
  /** 親メニューからログを開くための単調増加 signal。 */
  logOpenSignal?: number | undefined;
  /** ログボタンの表示範囲。外部メニューに移す場合は none にする。 */
  logButtonVisibility?: "all" | "desktop" | "none";
  /** visual_novel 背景レイヤー用に、現在表示中の dialogue speakerId を親へ通知する。 */
  onCurrentSpeakerChange?: ((speakerId: string | null) => void) | undefined;
  /** visual_novel 背景レイヤー用に、現在表示中の dialogue actor cue を親へ通知する。 */
  onCurrentActorCueChange?: ((cue: VisualNovelActorCue | null) => void) | undefined;
  /** visual_novel 背景レイヤー用に、選択肢 preview を親へ通知する。 */
  onChoicePreviewChange?: ((preview: ChoicePreview | null) => void) | undefined;
  /** visual_novel 背景レイヤー用に、選択肢表示中の思考カットイン候補を親へ通知する。 */
  onChoiceThinkingCueChange?: ((cue: ChoiceVisualCue | null) => void) | undefined;
  /** 生成待機中も選択肢パネルを残すため、選択済み choice を固定表示する。 */
  pendingChoiceId?: string | undefined;
  /** 生成待機中に表示し続ける side variant 用 prompt。 */
  pendingChoicePrompt?: string | undefined;
  /** Scenario の本文面色。choices や HUD には適用しない。 */
  panelBackgroundColor?: string | null | undefined;
};

// SceneView は presentational component として segments と分岐 UI を束ねる。
// usePerformAction を直接参照しないことで、親から注入されたコールバック経由でのみ副作用を起こし、
// テスト時に QueryClient / ApiClient を用意せずに済ませる。
//
// paragraph-reveal (Phase 4, Task 3.2) で `useSegmentReveal` hook を経由するように変更。
// 既存の sync 経路 (scene 1) でも segment 単位の逐次表示が default になり、
// readOnly=true / VITE_PARAGRAPH_REVEAL_ENABLED=false / motion-reduce のいずれかで
// 旧挙動 (全 segment 即時表示) にフォールバックする。
export function SceneView({
  scene,
  isPending,
  onSelectChoice,
  onAdvance,
  characters = [],
  visualAssets,
  readOnly = false,
  onLongPress,
  manualReveal = false,
  advanceSignal,
  clickAdvanceEnabled = true,
  segmentVariant = "default",
  segmentDisplayMode = "accumulate",
  logEnabled = false,
  logOpenSignal,
  logButtonVisibility = "all",
  onCurrentSpeakerChange,
  onCurrentActorCueChange,
  onChoicePreviewChange,
  onChoiceThinkingCueChange,
  pendingChoiceId,
  pendingChoicePrompt,
  panelBackgroundColor,
}: SceneViewProps): JSX.Element {
  const [logOpen, setLogOpen] = useState(false);
  const [currentModeInteractiveVisible, setCurrentModeInteractiveVisible] =
    useState(false);
  const lastLogOpenSignalRef = useRef<number | undefined>(logOpenSignal);
  // Character[] → Map<id, CharacterEntry> に変換。
  // characters 参照が変わらない限り再計算しないようメモ化する。
  const charactersMap = useMemo<Map<string, CharacterEntry>>(() => {
    return new Map(
      characters.map((c) => [c.id, { name: c.name, color: c.color, portraitUrl: c.portraitUrl ?? null }]),
    );
  }, [characters]);

  // env flag は ChoiceList / StreamingSceneView と同じ pattern (loadClientEnv + useMemo)。
  // readOnly=true (バックスクロール表示) では autoEnabled=false にして全 segment 即時表示。
  // paragraphRevealEnabled=false でも同様に旧挙動 (全表示) にフォールバックする。
  const env = useMemo(() => loadClientEnv(), []);
  const { preset, updatePreset } = useReaderStylePresetContext();
  const { visibleCount, advance, allVisible } = useSegmentReveal({
    segments: scene.segments,
    paused: false,
    autoEnabled: env.paragraphRevealEnabled && !readOnly && !manualReveal,
    manualEnabled: manualReveal && !readOnly,
    pacing: preset.pacing,
  });
  const lastAdvanceSignalRef = useRef<number | undefined>(advanceSignal);

  const usesCurrentSegmentDisplay = segmentDisplayMode === "current";
  const showCurrentModeInteractive = usesCurrentSegmentDisplay && currentModeInteractiveVisible;

  useEffect(() => {
    setCurrentModeInteractiveVisible(false);
  }, [scene.id, segmentDisplayMode]);

  useEffect(() => {
    if (!allVisible) {
      setCurrentModeInteractiveVisible(false);
    }
  }, [allVisible]);

  useEffect(() => {
    if (logOpenSignal === undefined) {
      lastLogOpenSignalRef.current = logOpenSignal;
      return;
    }
    const previous = lastLogOpenSignalRef.current;
    lastLogOpenSignalRef.current = logOpenSignal;
    if (previous === undefined || previous === logOpenSignal) {
      return;
    }
    if (logEnabled) {
      setLogOpen(true);
    }
  }, [logEnabled, logOpenSignal]);

  const advanceReveal = useCallback(() => {
    if (
      usesCurrentSegmentDisplay &&
      allVisible &&
      !currentModeInteractiveVisible &&
      !readOnly
    ) {
      setCurrentModeInteractiveVisible(true);
      return;
    }
    advance();
  }, [
    advance,
    allVisible,
    currentModeInteractiveVisible,
    readOnly,
    usesCurrentSegmentDisplay,
  ]);

  // クリック/タップで次のセグメントへ即座に進む。
  // 全セグメント表示済み (allVisible) の場合や readOnly モードでは hook 内で no-op となる。
  const handleClick = useCallback(() => {
    if (!clickAdvanceEnabled) return;
    advanceReveal();
  }, [advanceReveal, clickAdvanceEnabled]);

  useEffect(() => {
    if (advanceSignal === undefined) {
      lastAdvanceSignalRef.current = advanceSignal;
      return;
    }
    const previous = lastAdvanceSignalRef.current;
    lastAdvanceSignalRef.current = advanceSignal;
    if (previous === undefined || previous === advanceSignal) {
      return;
    }
    advanceReveal();
  }, [advanceSignal, advanceReveal]);

  const hasChoices = scene.choices.length > 0;
  const visibleSegments = scene.segments.slice(0, visibleCount);

  // 選択肢 / advance UI の表示条件は変更しない (要件 2.5)。
  // ただし allVisible 後にのみ表示することで bit-by-bit 互換 (旧挙動: visibleCount=segments.length 即値)。
  const showInteractive =
    !readOnly &&
    allVisible &&
    (!usesCurrentSegmentDisplay || showCurrentModeInteractive);
  const displayedSegments = usesCurrentSegmentDisplay && showInteractive
    ? []
    : usesCurrentSegmentDisplay
      ? visibleSegments.slice(-1)
      : visibleSegments;
  const currentDisplayedSegment =
    displayedSegments.length === 1 ? displayedSegments[0] : undefined;
  const currentDialogueSegment =
    segmentVariant === "visualNovel" &&
    currentDisplayedSegment?.type === "dialogue"
      ? currentDisplayedSegment
      : null;
  const currentSpeakerId = currentDialogueSegment?.speakerId ?? null;
  const currentActingVariantId = currentDialogueSegment?.actingVariantId;
  const currentCutinEnabled = currentDialogueSegment?.cutin;
  const currentCutinImageUrl = visualAssets?.mode === "versioned"
    ? currentCutinEnabled === true
      ? resolveCutinImageUrl(null, currentActingVariantId, visualAssets, currentSpeakerId ?? undefined) ?? undefined
      : undefined
    : currentDialogueSegment?.cutinImageUrl;
  const currentActorCue = useMemo<VisualNovelActorCue | null>(() => {
    if (currentSpeakerId === null) return null;
    return {
      speakerId: currentSpeakerId,
      ...(currentActingVariantId !== undefined
        ? { actingVariantId: currentActingVariantId }
        : {}),
      ...(currentCutinEnabled === true ? { cutin: true as const } : {}),
      ...(currentCutinImageUrl !== undefined
        ? { cutinImageUrl: currentCutinImageUrl }
        : {}),
    };
  }, [currentActingVariantId, currentCutinEnabled, currentCutinImageUrl, currentSpeakerId]);
  const showManualAdvanceCue =
    manualReveal &&
    visibleSegments.length > 0 &&
    (usesCurrentSegmentDisplay ? !showInteractive : !allVisible);
  const showLogButton =
    logEnabled && logButtonVisibility !== "none" && visibleSegments.length > 0;
  const choiceThinkingCue = useMemo<ChoiceVisualCue | null>(() => {
    if (!showInteractive || !hasChoices) return null;
    if (visualAssets?.mode === "versioned") return scene.choices.find((choice) => choice.visualCue !== undefined)?.visualCue ?? null;
    return resolveChoiceThinkingCue(scene.choices, characters[0]?.id);
  }, [characters, hasChoices, scene.choices, showInteractive, visualAssets]);
  const choicePrompt = useMemo(() => {
    if (!showInteractive || !hasChoices || !usesCurrentSegmentDisplay) return undefined;
    return resolveChoicePrompt({
      segments: scene.segments,
      choices: scene.choices,
      protagonist: characters[0],
    });
  }, [
    characters,
    hasChoices,
    scene.choices,
    scene.segments,
    showInteractive,
    usesCurrentSegmentDisplay,
  ]);

  const openLog = useCallback((event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setLogOpen(true);
  }, []);

  useEffect(() => {
    onCurrentSpeakerChange?.(currentSpeakerId);
    onCurrentActorCueChange?.(currentActorCue);
  }, [
    currentActorCue,
    currentSpeakerId,
    onCurrentActorCueChange,
    onCurrentSpeakerChange,
  ]);

  useEffect(() => {
    return () => {
      onCurrentSpeakerChange?.(null);
      onCurrentActorCueChange?.(null);
    };
  }, [onCurrentActorCueChange, onCurrentSpeakerChange]);

  useEffect(() => {
    onChoiceThinkingCueChange?.(choiceThinkingCue);
  }, [choiceThinkingCue, onChoiceThinkingCueChange]);

  useEffect(() => {
    return () => {
      onChoiceThinkingCueChange?.(null);
    };
  }, [onChoiceThinkingCueChange]);

  // mobile-pwa Phase 4.1: swipe-right で advance scene。
  // AdvanceButton が表示される条件 (= showInteractive && !hasChoices && !isPending) と同じく gate、
  // choices 表示中は不発火 (= reader に強制選択させる)、 readOnly では当然不発火。
  // tap (= threshold 50px 未満の移動) では既存 onClick の handleClick (= segment reveal) が走る。
  const swipeEnabled = showInteractive && !hasChoices && !isPending;
  const swipeHandlers = useSwipeGesture(
    swipeEnabled ? { onSwipeRight: onAdvance } : {},
  );

  // mobile-pwa Phase 4.3: long-press で通報 modal 起動。 readOnly のときは無効
  // (= backscroll 履歴は通報対象外、 親 PlayerScreen が onLongPress を未指定にすることで gating)。
  const longPressHandlers = useLongPress(
    onLongPress ?? (() => {}),
    { delayMs: 600 },
  );

  // mobile-pwa Phase 4.2: 2 指 pinch で reader-style-preset.fontSize を 3 段階切替。
  // fontSize は離散 enum (= small / medium / large) のため scale を直接 px 変換せず
  // step 関数で隣接段階へ移動する。 readOnly でも有効 (= backscroll でも文字サイズ
  // 調整したい want)、 LocalStorage に即時保存される (= 既存 updatePreset の動作)。
  const stepFontSize = useCallback(
    (direction: "up" | "down") => {
      const order: ReaderFontSize[] = ["small", "medium", "large"];
      const currentIndex = order.indexOf(preset.fontSize);
      if (currentIndex === -1) return;
      const nextIndex =
        direction === "up"
          ? Math.min(order.length - 1, currentIndex + 1)
          : Math.max(0, currentIndex - 1);
      if (nextIndex === currentIndex) return;
      const nextFontSize = order[nextIndex];
      if (nextFontSize === undefined) return;
      updatePreset({ fontSize: nextFontSize });
    },
    [preset.fontSize, updatePreset],
  );
  const pinchHandlers = usePinchZoom({
    onZoomIn: () => stepFontSize("up"),
    onZoomOut: () => stepFontSize("down"),
  });

  // tap / swipe / long-press / pinch は各々独立 timer / threshold で排他。 props spread
  // 順序は重複 prop (= onTouchStart 等) で long-press が上書き優先となるが、 useSwipeGesture
  // も touchstart で startRef 記録するだけで長押し干渉せず、 useLongPress も touchstart で
  // timer 開始するだけ。 usePinchZoom は 2 指接触時のみ pinch 候補とし 1 指 swipe / tap と
  // 排他 (= touches.length !== 2 で reset)。 両方の onTouchStart を呼ぶ手動 merge は不要
  // (= test で確認)。
  const combinedTouchStart = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      swipeHandlers.onTouchStart(event);
      longPressHandlers.onTouchStart(event);
      pinchHandlers.onTouchStart(event);
    },
    [swipeHandlers, longPressHandlers, pinchHandlers],
  );
  const combinedTouchMove = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      longPressHandlers.onTouchMove(event);
      pinchHandlers.onTouchMove(event);
    },
    [longPressHandlers, pinchHandlers],
  );
  const combinedTouchEnd = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      swipeHandlers.onTouchEnd(event);
      longPressHandlers.onTouchEnd(event);
      pinchHandlers.onTouchEnd(event);
    },
    [swipeHandlers, longPressHandlers, pinchHandlers],
  );

  return (
    <div
      data-testid="scene-view"
      className="flex flex-col gap-4 animate-[fadeIn_0.3s_ease-out] motion-reduce:animate-none"
      onClick={handleClick}
      onTouchStart={combinedTouchStart}
      onTouchEnd={combinedTouchEnd}
      onTouchMove={combinedTouchMove}
      onTouchCancel={longPressHandlers.onTouchCancel}
    >
      {showLogButton && (
        <div className="flex justify-end">
          <button
            type="button"
            data-testid="visual-novel-log-button"
            onClick={openLog}
            className={
              (logButtonVisibility === "desktop" ? "hidden md:inline-flex " : "") +
              "min-h-9 items-center rounded border border-reader-border-strong bg-reader-surface px-3 py-1.5 text-sm text-reader-secondary hover:bg-reader-border/60 hover:text-reader-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-reader-focus-ring"
            }
          >
            ログ
          </button>
        </div>
      )}
      {displayedSegments.map((segment, index) => (
        // segment には安定した id が無いため index + type + 先頭テキストの組み合わせを key に使う。
        // 同一 scene 内で segments が再配置される想定はないため index ベースで十分。
        <SegmentRenderer
          key={`${usesCurrentSegmentDisplay ? visibleCount - displayedSegments.length + index : index}-${segment.type}`}
          segment={segment}
          charactersMap={charactersMap}
          visualAssets={visualAssets}
          variant={segmentVariant}
          panelBackgroundColor={panelBackgroundColor}
        />
      ))}
      {showManualAdvanceCue && (
        <div
          data-testid="visual-novel-advance-cue"
          className="flex justify-end pr-1 pt-1"
          aria-hidden="true"
        >
          <span className="block size-2 rotate-45 border-b-2 border-r-2 border-reader-muted opacity-70" />
        </div>
      )}
      {/* readOnly モード時は選択 UI を非表示にする（過去シーンの読み取り専用表示） */}
      {showInteractive && (
        hasChoices ? (
          <>
            {!usesCurrentSegmentDisplay && (
              <hr className="border-reader-border opacity-40 my-1" />
            )}
            <ChoiceList
              choices={scene.choices}
              isPending={isPending}
              onSelect={onSelectChoice}
              chosenChoiceId={pendingChoiceId}
              charactersMap={charactersMap}
              variant={usesCurrentSegmentDisplay ? "side" : "default"}
              {...(usesCurrentSegmentDisplay ? { choicePrompt: pendingChoicePrompt ?? choicePrompt } : {})}
              {...(usesCurrentSegmentDisplay && onChoicePreviewChange !== undefined
                ? { onPreviewChoiceChange: onChoicePreviewChange }
                : {})}
            />
          </>
        ) : (
          <AdvanceButton isPending={isPending} onAdvance={onAdvance} />
        )
      )}
      {logOpen && (
        <VisualNovelLogModal
          segments={visibleSegments}
          charactersMap={charactersMap}
          onClose={() => { setLogOpen(false); }}
          visualAssets={visualAssets}
        />
      )}
    </div>
  );
}
